import { EmployeeDto, OnboardingPeriodDto } from '@zorro/clients';
import { Card } from '@zorro/design-system/v2/Card';
import { Title } from '@zorro/design-system/v2/Title';
import { Stack } from '@zorro/web/design-system';
import { useRoles } from '@zorro/web/ui-utils';

import { PaymentDetails } from './PaymentDetails/PaymentDetails';
import { PaymentMethodViewer } from './PaymentDetails/paymentMethodDisplayRules';
import { EmployeePaymentMethodDetails } from './PaymentMethod/EmployeePaymentMethodDetails';
import { ManagePaymentMethodStatus } from './PaymentMethod/ManagePaymentMethodStatus';
import { PaymentAmountDistribution } from './PaymentMethod/PaymentAmountDistribution';
import { PaymentMethodType } from './PaymentMethod/PaymentMethodType';
import { EmployeePaymentStatus } from './PaymentStatus/EmployeePaymentStatus';
import { LynxBankingDisclaimer } from './ZorroPay/LynxBankingDisclaimer';
import { useZorroPayActivationStatus } from './ZorroPay/hooks/useZorroPayActivationStatus';
import { PAYMENT_SECTION_WIDTH } from './paymentsDesign.consts';

type PaymentsTabContentProps = {
  employeeId: EmployeeDto['id'];
  employerId: string;
  onboardingPeriodId: OnboardingPeriodDto['id'];
  shouldHidePaymentStatuses: boolean;
  isManagementEnabled?: boolean;
  isLynxEnabled?: boolean;
  isDon?: boolean;
  paymentStatusVariant?: 'default' | 'elena' | 'dashboard';
  shouldShowCompletedPaymentStatus?: boolean;
  shouldMakePaymentStatusVariantOutline?: boolean;
  viewer?: PaymentMethodViewer;
  shouldShowBankingDisclaimer?: boolean;
};

const PaymentsTabContent = ({
  employeeId,
  employerId,
  onboardingPeriodId,
  isManagementEnabled = false,
  isLynxEnabled = false,
  shouldHidePaymentStatuses,
  isDon = false,
  paymentStatusVariant,
  shouldShowCompletedPaymentStatus,
  shouldMakePaymentStatusVariantOutline,
  viewer,
  shouldShowBankingDisclaimer = false,
}: PaymentsTabContentProps) => {
  const { isZorroOperations } = useRoles();

  const { isActivated } = useZorroPayActivationStatus({
    employeeId,
    enabled: isLynxEnabled,
  });

  return (
    <Stack gap="xl" maw={900}>
      <Stack gap={isLynxEnabled ? '1.5rem' : 'sm'}>
        {!isLynxEnabled && (
          <Title order={2} size="sm">
            Payment method
          </Title>
        )}
        {isLynxEnabled ? (
          viewer && (
            <Stack gap="1.5rem" maw={PAYMENT_SECTION_WIDTH}>
              <PaymentDetails
                viewer={viewer}
                employeeId={employeeId}
                employerId={employerId}
                onboardingPeriodId={onboardingPeriodId}
              />
            </Stack>
          )
        ) : (
          <Card variant="white-border" maw={PAYMENT_SECTION_WIDTH}>
            <Stack>
              <PaymentMethodType
                employeeId={employeeId}
                onboardingPeriodId={onboardingPeriodId}
                isElena={isDon}
              />
              <Stack>
                <PaymentAmountDistribution
                  employeeId={employeeId}
                  onboardingPeriodId={onboardingPeriodId}
                />
                <EmployeePaymentMethodDetails
                  employeeId={employeeId}
                  onboardingPeriodId={onboardingPeriodId}
                  isPaymentsTab
                  isElena={isDon}
                />
              </Stack>
              {isManagementEnabled && isZorroOperations && (
                <ManagePaymentMethodStatus
                  employeeId={employeeId}
                  onboardingPeriodId={onboardingPeriodId}
                />
              )}
            </Stack>
          </Card>
        )}
      </Stack>
      {!shouldHidePaymentStatuses && (
        <EmployeePaymentStatus
          onboardingPeriodId={onboardingPeriodId}
          variant={paymentStatusVariant}
          shouldShowCompletedStatus={shouldShowCompletedPaymentStatus}
          shouldMakeVariantOutline={shouldMakePaymentStatusVariantOutline}
          shouldShowManageButton={isManagementEnabled}
        />
      )}
      {shouldShowBankingDisclaimer && isLynxEnabled && isActivated && (
        <LynxBankingDisclaimer />
      )}
    </Stack>
  );
};

export { PaymentsTabContent };
